import { Trophy, Plus, Users, ToggleLeft, ToggleRight, Trash2, CheckCircle, XCircle } from "lucide-react";

export function DashboardChallengesTab({
  challenges, showChallengeForm, setShowChallengeForm,
  newChallenge, setNewChallenge, challengeLoading,
  handleCreateChallenge, handleToggleChallenge, handleDeleteChallenge,
  handleViewSubmissions, selectedChallenge, submissions,
  handleApproveSubmission, handleRejectSubmission,
}) {
  return (
    <div className="space-y-4">
      <button
        onClick={() => setShowChallengeForm(!showChallengeForm)}
        className="w-full bg-card border border-dashed border-[#FF6B35]/40 rounded-2xl p-4 flex items-center justify-center gap-2 text-[#FF6B35] hover:bg-[#FF6B35]/5 transition-colors"
        data-testid="create-challenge-btn"
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm font-bold">Creer un defi</span>
      </button>

      {showChallengeForm && (
        <div className="bg-card border border-border rounded-3xl p-5 space-y-4 shadow-sm" data-testid="challenge-form">
          <h3 className="font-heading font-bold text-sm text-card-foreground">Nouveau defi</h3>
          <input
            placeholder="Titre du defi"
            value={newChallenge.title}
            onChange={(e) => setNewChallenge({ ...newChallenge, title: e.target.value })}
            className="w-full bg-white border border-border rounded-xl px-4 py-3 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none"
            data-testid="challenge-title-input"
          />
          <textarea
            placeholder="Instructions pour les participants"
            value={newChallenge.description}
            onChange={(e) => setNewChallenge({ ...newChallenge, description: e.target.value })}
            className="w-full bg-white border border-border rounded-xl px-4 py-3 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none resize-none h-20"
            data-testid="challenge-desc-input"
          />
          <div>
            <p className="text-xs text-muted-foreground mb-2">Type</p>
            <div className="flex flex-wrap gap-1.5">
              {[
                { key: "share", label: "Partager" },
                { key: "create", label: "Creer" },
                { key: "listen", label: "Ecouter" },
                { key: "attend", label: "Participer" },
                { key: "invite", label: "Inviter" },
                { key: "custom", label: "Autre" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setNewChallenge({ ...newChallenge, challenge_type: t.key })}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                    newChallenge.challenge_type === t.key
                      ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]"
                      : "bg-white border-border text-muted-foreground"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Credits a gagner</p>
              <input
                type="number" min={1}
                value={newChallenge.reward_credits}
                onChange={(e) => setNewChallenge({ ...newChallenge, reward_credits: parseInt(e.target.value) || 1 })}
                className="w-full bg-white border border-border rounded-xl px-4 py-2 text-sm text-card-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none"
                data-testid="challenge-credits-input"
              />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Max completions (-1 = illimite)</p>
              <input
                type="number" min={-1}
                value={newChallenge.max_completions}
                onChange={(e) => setNewChallenge({ ...newChallenge, max_completions: parseInt(e.target.value) || -1 })}
                className="w-full bg-white border border-border rounded-xl px-4 py-2 text-sm text-card-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none"
                data-testid="challenge-max-input"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreateChallenge}
              disabled={challengeLoading || !newChallenge.title.trim()}
              className="bg-[#FF6B35] text-white font-bold px-6 py-2.5 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
              data-testid="challenge-submit-btn"
            >
              {challengeLoading ? "..." : "Creer"}
            </button>
            <button
              onClick={() => setShowChallengeForm(false)}
              className="bg-muted text-muted-foreground px-4 py-2.5 rounded-full text-sm border border-border"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {challenges.length === 0 ? (
        <div className="text-center py-12">
          <Trophy className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Aucun defi cree</p>
          <p className="text-xs text-muted-foreground mt-1">Lance un defi a tes fans</p>
        </div>
      ) : (
        challenges.map((c) => (
          <div key={c.id} className={`bg-card border rounded-2xl p-4 shadow-sm ${c.active ? "border-border" : "border-red-200 opacity-60"}`} data-testid={`artist-challenge-${c.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#7000FF]/15 flex items-center justify-center shrink-0">
                <Trophy className="w-5 h-5 text-[#7000FF]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm text-card-foreground truncate">{c.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="font-mono-credits font-bold text-xs text-[#FF6B35]">+{c.reward_credits} credits</span>
                  <span className="text-[10px] text-muted-foreground">{c.participants_count} participants</span>
                  <span className="text-[10px] text-muted-foreground">{c.completions_count} valides</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => handleViewSubmissions(c.id)} className="p-1.5 rounded-lg text-muted-foreground hover:text-[#7000FF] hover:bg-[#7000FF]/5 transition-colors" title="Voir les soumissions">
                  <Users className="w-4 h-4" />
                </button>
                <button onClick={() => handleToggleChallenge(c.id)} className={`p-1.5 rounded-lg transition-colors ${c.active ? "text-emerald-500 hover:bg-emerald-50" : "text-red-400 hover:bg-red-50"}`}>
                  {c.active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                </button>
                <button onClick={() => handleDeleteChallenge(c.id)} className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {selectedChallenge === c.id && (
              <div className="mt-3 pt-3 border-t border-border space-y-2">
                <p className="text-xs font-bold text-card-foreground">Soumissions ({submissions.length})</p>
                {submissions.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Aucune soumission</p>
                ) : (
                  submissions.map((s) => (
                    <div key={s.id} className="flex items-center gap-2 p-2 bg-white rounded-xl border border-border">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-card-foreground">{s.username}</p>
                        {s.proof_text && <p className="text-[10px] text-card-foreground/80 mt-0.5">{s.proof_text}</p>}
                        {s.proof_url && <a href={s.proof_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#FF6B35] underline">Lien</a>}
                      </div>
                      {s.status === "pending" ? (
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => handleApproveSubmission(c.id, s.id)} className="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-50" data-testid={`approve-${s.id}`}><CheckCircle className="w-4 h-4" /></button>
                          <button onClick={() => handleRejectSubmission(c.id, s.id)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50" data-testid={`reject-${s.id}`}><XCircle className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <span className={`text-[9px] px-2 py-0.5 rounded-full shrink-0 ${s.status === "approved" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
                          {s.status === "approved" ? "Valide" : "Refuse"}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
